import { Box } from "@mui/material";
import type { BoxProps } from "@mui/material/Box";
import type { ReactNode } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// NodeDrawer — inline form panel that sits beside the canvas
// ─────────────────────────────────────────────────────────────────────────────
//
// Was: MUI <Drawer> (modal overlay with backdrop dim and box-shadow that
// floats over the canvas).
// Now: a regular layout panel — same flex row as the toolbox + canvas, no
// backdrop, no shadow. When `open` is false, the panel collapses (width 0
// + opacity 0). When true, it expands to its target width.
//
// Form is in the SAME layer as the canvas — clicking outside doesn't dismiss
// it via backdrop, and the canvas remains fully interactive while the form
// is open. The Cancel / Save buttons handle close.
// ─────────────────────────────────────────────────────────────────────────────

export interface NodeDrawerProps extends Omit<BoxProps, "children"> {
	open: boolean;
	onClose?: () => void;
	children: ReactNode;
	width?: number | string;
	/** Anchor side for slide-in animation (visual only — layout is inline). */
	anchor?: "left" | "right";
}

const NodeDrawer = (props: NodeDrawerProps) => {
	const {
		open,
		children,
		// Now that the parent panel is user-resizable, the form fills the
		// container. Width still has a default so callers without a parent
		// resizer (e.g. previews) still render at a sensible size.
		width = "100%",
		// Filter out leftover Drawer-only props (consumers may still pass them)
		// so they don't end up on the underlying <Box> as DOM attributes.
		onClose: _onClose,
		...boxProps
	} = props as NodeDrawerProps & { [key: string]: unknown };

	// Render nothing when closed so the panel doesn't take layout space and
	// stack on top of siblings (used to push the canvas off-screen).
	if (!open) return null;

	// Strip any remaining MUI Drawer-specific props the legacy callers pass.
	const cleanProps = { ...boxProps } as Record<string, unknown>;
	delete cleanProps.PaperProps;
	delete cleanProps.slotProps;
	delete cleanProps.ModalProps;
	delete cleanProps.BackdropProps;
	delete cleanProps.disableEnforceFocus;
	delete cleanProps.anchor;

	return (
		<Box
			{...(cleanProps as BoxProps)}
			sx={{
				// The parent wrapper (RuleBuilder's leftPane Box) is flex column
				// with a fixed PIXEL width. So the form fills the parent's full
				// height (flex: 1 + minHeight: 0) and 100% of its width — never
				// `flex: 0 0 ${px}` which in a column container would clamp the
				// HEIGHT to that pixel value.
				flex: 1,
				width: "100%",
				maxWidth: "100%",
				minWidth: 0,
				minHeight: 0,
				background: "#FFFFFF",
				// No side borders on the form panel itself. The full-height
				// LeftPanelResizer rail to the right already separates the form
				// from the canvas; the previous borderLeft here produced a second
				// vertical line against the nav, so it has been removed.
				borderLeft: "none",
				borderRight: "none",
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
				...((cleanProps.sx as object) || {})
			}}
		>
			{children}
		</Box>
	);
};

export default NodeDrawer;
